import { useEffect, useRef, useState } from "react";

function clockTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const rounded = Math.floor(seconds);
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const remainder = rounded % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`
    : `${minutes}:${String(remainder).padStart(2, "0")}`;
}

export function youtubeVideoId(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.hostname === "youtu.be") return url.pathname.split("/").filter(Boolean)[0] ?? null;
    if (url.hostname.endsWith("youtube.com")) {
      if (url.pathname === "/watch") return url.searchParams.get("v");
      const parts = url.pathname.split("/").filter(Boolean);
      if (["embed", "shorts", "live"].includes(parts[0] ?? "")) return parts[1] ?? null;
    }
  } catch {
    return null;
  }
  return null;
}

export function PodcastPlayer({
  audioUrl,
  youtubeUrl,
  title,
  episodeId,
}: {
  audioUrl: string | null;
  youtubeUrl: string | null;
  title: string;
  episodeId?: string;
}): React.JSX.Element | null {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);
  const [failed, setFailed] = useState(false);
  const videoId = youtubeVideoId(youtubeUrl);
  const milestones = useRef(new Set<string>());

  function recordPlayback(eventType: "audio_start" | "audio_25" | "audio_50" | "audio_75" | "audio_complete", progress: number): void {
    if (!episodeId || milestones.current.has(eventType)) return;
    milestones.current.add(eventType);
    void fetch("/api/events", {
      method: "POST", credentials: "same-origin", keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eventType, contentType: "podcast", contentId: episodeId, source: "player", progress }),
    }).then((response) => { if (!response.ok) milestones.current.delete(eventType); })
      .catch(() => milestones.current.delete(eventType));
  }

  useEffect(() => {
    setPlaying(false);
    setPosition(0);
    setDuration(0);
    setFailed(false);
    milestones.current.clear();
  }, [audioUrl]);

  if (audioUrl && !failed) {
    return (
      <section className="podcast-player" aria-label="原节目音频播放器">
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="metadata"
          onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
          onTimeUpdate={(event) => {
            const audio = event.currentTarget;
            setPosition(audio.currentTime);
            if (!audio.paused && Number.isFinite(audio.duration) && audio.duration > 0) {
              const progress = Math.min(1, audio.currentTime / audio.duration);
              for (const [threshold, name] of [[0.25, "audio_25"], [0.5, "audio_50"], [0.75, "audio_75"]] as const) {
                if (progress >= threshold) recordPlayback(name, progress);
              }
            }
          }}
          onPlay={() => { setPlaying(true); recordPlayback("audio_start", 0); }}
          onPause={() => setPlaying(false)}
          onEnded={() => { setPlaying(false); recordPlayback("audio_complete", 1); }}
          onError={() => setFailed(true)}
        />
        <button
          className="podcast-player-toggle"
          data-analytics-feature={playing ? "podcast_pause" : "podcast_play"}
          aria-label={playing ? "暂停" : "播放"}
          onClick={() => {
            const audio = audioRef.current;
            if (!audio) return;
            if (audio.paused) void audio.play();
            else audio.pause();
          }}
        >
          {playing ? "Ⅱ" : "▶"}
        </button>
        <div className="podcast-player-body">
          <div className="podcast-player-heading">
            <span>原节目音频</span>
            <small>{title}</small>
          </div>
          <input
            type="range"
            min="0"
            max={Math.max(duration, 1)}
            step="1"
            value={Math.min(position, Math.max(duration, 1))}
            aria-label="播放进度"
            onChange={(event) => {
              const next = Number(event.target.value);
              if (audioRef.current) audioRef.current.currentTime = next;
              setPosition(next);
            }}
          />
          <div className="podcast-player-time">
            <span>{clockTime(position)} / {clockTime(duration)}</span>
            <button
              aria-label="切换播放速度"
              data-analytics-feature="podcast_speed"
              onClick={() => {
                const rates = [1, 1.25, 1.5, 2];
                const next = rates[(rates.indexOf(rate) + 1) % rates.length];
                setRate(next);
                if (audioRef.current) audioRef.current.playbackRate = next;
              }}
            >
              {rate}×
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (!videoId) return null;
  return (
    <section className="podcast-video-player" aria-label="原节目视频播放器">
      <div className="podcast-player-heading">
        <span>原节目视频</span>
        <small>YouTube 播放需可访问外网</small>
      </div>
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0`}
        title={`${title} · 原节目视频`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </section>
  );
}
